/**
 * La scheda «Partner»: chi sono, in che stato, con quale profilo e regime, quante aziende hanno portato. Un clic apre
 * la modifica; il menu della riga fa le azioni che dipendono dallo stato (invito, sospensione, ricalcolo) e apre lo
 * spazio del partner come lo vede lui, in sola lettura («Guarda il suo spazio», 03/11/2026).
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Button, IconButton, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Add, MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { SoftChip, type SoftChipTone } from '../SoftChip';
import type { PartnerDoc, StatoPartner } from '@shared/partner/tipi';
import {
  apriSpazioDelPartner, cambiaStatoPartner, invitaPartner, ricalcola, type ConId, type DatiProgrammaPartner,
} from '../../services/partnerConsoleService';
import { DialogoPartner } from './DialogoPartner';
import type { Esegui } from './esegui';

export const TONO_PARTNER: Record<StatoPartner, SoftChipTone> = {
  invitato: 'info',
  attivo: 'success',
  sospeso: 'warning',
  cessato: 'default',
};

interface Props {
  dati: DatiProgrammaPartner;
  /** Esegue un'azione, dice com'è andata e ricarica; `true` se è riuscita. */
  esegui: Esegui;
  onErrore: (messaggio: string) => void;
  onRicarica: () => void;
}

export const SchedaPartner: React.FC<Props> = ({ dati, esegui, onErrore, onRicarica }) => {
  const { t } = useTranslation('partner');
  const [inModifica, setInModifica] = useState<ConId<PartnerDoc> | null | undefined>(undefined);
  const [menu, setMenu] = useState<{ ancora: HTMLElement; partner: ConId<PartnerDoc> } | null>(null);

  const nomi = useMemo(() => ({
    profili: new Map(dati.profili.map((p) => [p.id, p.nome])),
    regimi: new Map(dati.regimi.map((r) => [r.id, r.nome])),
  }), [dati.profili, dati.regimi]);

  const aziendePerPartner = useMemo(() => {
    const conta = new Map<string, number>();
    for (const a of dati.abbinamenti) {
      if (a.stato === 'attiva' || a.stato === 'registrata' || a.stato === 'da_decidere') conta.set(a.partner_id, (conta.get(a.partner_id) ?? 0) + 1);
    }
    return conta;
  }, [dati.abbinamenti]);

  const elenco = useMemo(() => [...dati.partner].sort((a, b) => a.nome.localeCompare(b.nome)), [dati.partner]);

  const azione = <T,>(fai: () => Promise<T>, riuscito?: string | ((r: T) => string)) => {
    setMenu(null);
    void esegui(fai, riuscito);
  };

  const voci = (p: ConId<PartnerDoc>) => {
    const v: Array<{ chiave: string; etichetta: string; fai: () => void }> = [];
    v.push({ chiave: 'modifica', etichetta: t('azioni.modifica'), fai: () => { setMenu(null); setInModifica(p); } });
    v.push({
      chiave: 'guarda',
      etichetta: t('azioni.guarda_spazio'),
      fai: () => azione(() => apriSpazioDelPartner(p.id), (r) => t('messaggi.spazio_aperto', { nome: r.partner_nome })),
    });
    if (p.stato === 'invitato') {
      v.push({
        chiave: 'invita',
        etichetta: p.invito ? t('azioni.reinvita') : t('azioni.invita'),
        fai: () => azione(async () => {
          const r = await invitaPartner(p.id);
          if (!r.inviato) throw new Error(t('messaggi.invito_non_inviato'));
        }, t('messaggi.invito_inviato', { email: p.email })),
      });
    }
    if (p.stato === 'attivo') v.push({ chiave: 'sospendi', etichetta: t('azioni.sospendi'), fai: () => azione(() => cambiaStatoPartner(p.id, 'sospeso')) });
    if (p.stato === 'sospeso') v.push({ chiave: 'riattiva', etichetta: t('azioni.riattiva'), fai: () => azione(() => cambiaStatoPartner(p.id, 'attivo')) });
    if (p.stato !== 'cessato') v.push({ chiave: 'cessa', etichetta: t('azioni.cessa'), fai: () => azione(() => cambiaStatoPartner(p.id, 'cessato')) });
    v.push({
      chiave: 'ricalcola',
      etichetta: t('azioni.ricalcola'),
      fai: () => azione(() => ricalcola(p.id), (e) => t('messaggi.ricalcolo_fatto', {
        aziende: e.aziende_controllate, movimenti: e.movimenti_nuovi, bozze: e.bozze,
      })),
    });
    return v;
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setInModifica(null)} sx={{ textTransform: 'none' }}>
          {t('azioni.nuovo_partner')}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflow: 'hidden' }}>
        {elenco.length === 0 ? (
          <Typography variant="body2" sx={{ p: 4, color: tokens.color.ash, textAlign: 'center' }}>{t('vuoti.partner')}</Typography>
        ) : (
          <Table size="small" sx={{ '& td': { py: 1.5 }, '& tr:last-child td': { borderBottom: 0 }, '& tbody tr': { cursor: 'pointer' } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('colonne.partner')}</TableCell>
                <TableCell>{t('colonne.stato')}</TableCell>
                <TableCell>{t('colonne.profilo')}</TableCell>
                <TableCell>{t('colonne.regime')}</TableCell>
                <TableCell>{t('colonne.aziende')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {elenco.map((p) => (
                <TableRow key={p.id} hover onClick={() => setInModifica(p)}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite }}>{p.nome}</Typography>
                    {p.dimostrativo === true && <Box sx={{ mt: 0.5 }}><SoftChip label={t('partner.dimostrativo')} tone="default" /></Box>}
                    <Typography variant="caption" sx={{ color: tokens.color.ash, display: 'block' }}>{p.email}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block' }}>{t('partner.codice', { codice: p.codice })}</Typography>
                  </TableCell>
                  <TableCell>
                    <SoftChip label={t(`stato_partner.${p.stato}`)} tone={TONO_PARTNER[p.stato]} />
                    <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 0.5 }}>
                      {p.uid ? t('partner.entrato') : t('partner.non_entrato')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{p.senza_provvigioni ? t('partner.senza_provvigioni') : (p.profilo_compenso_id ? (nomi.profili.get(p.profilo_compenso_id) ?? '—') : '—')}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                      {p.liquidazione_ogni_mesi ? t('partner.ogni_mesi', { count: p.liquidazione_ogni_mesi }) : t('partner.frequenza_piattaforma')}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{p.regime_fiscale_id ? (nomi.regimi.get(p.regime_fiscale_id) ?? '—') : t('partner.regime_da_scegliere')}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2">{t('partner.aziende_conteggio', { count: aziendePerPartner.get(p.id) ?? 0 })}</Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={t('azioni.altre_azioni')}>
                      <IconButton size="small" onClick={(e) => setMenu({ ancora: e.currentTarget, partner: p })}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu anchorEl={menu?.ancora} open={!!menu} onClose={() => setMenu(null)}>
        {menu && voci(menu.partner).map((v) => (
          <MenuItem key={v.chiave} onClick={v.fai} sx={{ fontSize: '0.85rem' }}>{v.etichetta}</MenuItem>
        ))}
      </Menu>

      <DialogoPartner
        aperto={inModifica !== undefined}
        partner={inModifica ?? null}
        profili={dati.profili}
        regimi={dati.regimi}
        onChiudi={() => setInModifica(undefined)}
        onSalvato={onRicarica}
        onErrore={onErrore}
      />
    </Box>
  );
};
